import { ReactEventHandler, useCallback } from 'react';

import { VisitDocumentTemplateHtmlContent } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormController, StyledFlex } from '@dreryk/react-components';

import { StyledFrame } from './DocumentTemplateForm.styled';
import {
   DocumentTemplateFormBean,
   DocumentTemplateFormController,
   DocumentTemplatePreviewValues,
   TagActions,
} from './DocumentTemplateForm.types';
import {
   extractDocumentTemplateValues,
   insertDocumentTemplateValues,
   setDocumentTemplateElementsChangeEvent,
} from './DocumentTemplateForm.utils';

type DocumentTemplateFormRenderProps = Pick<
   DocumentTemplateFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'getValue' | 'trigger' | 'markPristine'
>;

type DocumentTemplateFormProps = {
   readonly documentTemplate: VisitDocumentTemplateHtmlContent;
   readonly documentTemplateDraft?: DocumentTemplateFormBean | null;
   readonly tagActions?: TagActions;
   readonly children: (renderProps: DocumentTemplateFormRenderProps) => JSX.Element;
};

export const DocumentTemplateForm = (props: DocumentTemplateFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      handleSubmitDraft,
      formState,
      setValue,
      getValue,
      trigger,
      markPristine,
   } = useFormController<DocumentTemplateFormBean>({
      documentTemplateValues: props.documentTemplateDraft?.documentTemplateValues ?? {},
   });

   const handleLoad = useCallback(
      (documentTemplateValues: DocumentTemplatePreviewValues): ReactEventHandler<HTMLIFrameElement> => (event) => {
         const editorElement = event.currentTarget.contentWindow!.document.body;
         insertDocumentTemplateValues(editorElement, documentTemplateValues);

         const realDataValues = extractDocumentTemplateValues(editorElement);
         setValue('documentTemplateValues', { ...realDataValues, ...documentTemplateValues });

         setDocumentTemplateElementsChangeEvent(
            editorElement,
            (id, value) => {
               setValue(
                  'documentTemplateValues',
                  {
                     ...getValue('documentTemplateValues'),
                     [id]: value,
                  },
                  {
                     shouldDirty: true,
                  }
               );
            },
            props.tagActions
         );
      },
      [getValue, setValue, props.tagActions]
   );

   return (
      <>
         <StyledFlex as={'form'} flex={'1'} noValidate autoComplete='off'>
            <FormController<DocumentTemplatePreviewValues> controller={register('documentTemplateValues')}>
               {({ value }) => <StyledFrame srcDoc={props.documentTemplate.content} onLoad={handleLoad(value)} />}
            </FormController>
         </StyledFlex>

         {props.children({
            handleSubmit: handleSubmit,
            handleSubmitDraft: handleSubmitDraft,
            formState: formState,
            getValue: getValue,
            trigger: trigger,
            markPristine: markPristine,
         })}
      </>
   );
};
